import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RippleButton } from "@/components/interactions/RippleButton";
import { ScreenTransition } from "@/components/interactions/ScreenTransition";
import { EmptyState } from "@/components/interactions/EmptyState";
import { LoadingState, ContentLoader } from "@/components/interactions/LoadingState";
import { ErrorState, InlineError } from "@/components/interactions/ErrorState";
import { SuccessCheckmark, StreakFire, Confetti } from "@/components/interactions/SuccessFeedback";
import { PointsPopup } from "@/components/gamification/PointsPopup";
import { PullToRefresh } from "@/components/interactions/PullToRefresh";
import { Camera, Image, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "@/hooks/use-toast";

export const MicroInteractionsShowcase = () => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [buttonSuccess, setButtonSuccess] = useState(false);
  const [showCheckmark, setShowCheckmark] = useState(false);
  const [showPoints, setShowPoints] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const handleAsyncAction = async () => {
    setButtonLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setButtonLoading(false);
    setButtonSuccess(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setButtonSuccess(false);
  };

  const handleShowSuccess = () => {
    setShowCheckmark(true);
  };

  const handleShowPoints = () => {
    setShowPoints(true);
  };

  const handleShowConfetti = () => {
    setShowConfetti(true);
  };

  const handleRefresh = async () => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast({
      title: "Refreshed!",
      description: "Data has been updated",
    });
  };

  return (
    <div className="p-4 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Micro-Interactions Showcase</h1>
        <p className="text-muted-foreground">
          Test all polish and micro-interactions in the app
        </p>
      </div>

      <Tabs defaultValue="buttons" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="buttons">Buttons</TabsTrigger>
          <TabsTrigger value="states">States</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
          <TabsTrigger value="transitions">Transitions</TabsTrigger>
        </TabsList>

        {/* Buttons Tab */}
        <TabsContent value="buttons" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Button Interactions</CardTitle>
              <CardDescription>
                Ripple effect, scale down on press, haptic feedback, loading & success states
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm font-medium">Basic Buttons</p>
                <div className="flex flex-wrap gap-2">
                  <RippleButton variant="default">Default</RippleButton>
                  <RippleButton variant="primary">Primary</RippleButton>
                  <RippleButton variant="secondary">Secondary</RippleButton>
                  <RippleButton variant="outline">Outline</RippleButton>
                  <RippleButton variant="ghost">Ghost</RippleButton>
                  <RippleButton variant="danger">Danger</RippleButton>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Async Button States</p>
                <div className="flex flex-wrap gap-2">
                  <RippleButton
                    isLoading={buttonLoading}
                    isSuccess={buttonSuccess}
                    onClick={handleAsyncAction}
                  >
                    {buttonSuccess ? "Done!" : "Click Me"}
                  </RippleButton>
                  <RippleButton isLoading={true}>Loading...</RippleButton>
                  <RippleButton disabled>Disabled</RippleButton>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Button Sizes</p>
                <div className="flex items-center flex-wrap gap-2">
                  <RippleButton size="sm">Small</RippleButton>
                  <RippleButton size="default">Default</RippleButton>
                  <RippleButton size="lg">Large</RippleButton>
                  <RippleButton size="icon">
                    <Camera className="w-4 h-4" />
                  </RippleButton>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* States Tab */}
        <TabsContent value="states" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Empty States</CardTitle>
              <CardDescription>
                Friendly illustrations and helpful messages
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EmptyState
                icon={Image}
                title="No photos yet"
                description="Start capturing your farm activities to build your timeline and earn points!"
                action={{
                  label: "Take Photo",
                  onClick: () => toast({ title: "Opening camera..." }),
                  icon: Camera,
                }}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Loading States</CardTitle>
              <CardDescription>
                Skeleton screens and loaders
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm font-medium mb-2">Full Screen Loader</p>
                <div className="border border-border rounded-lg h-40 relative overflow-hidden">
                  <LoadingState message="Loading activities..." />
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Content Loader</p>
                <ContentLoader lines={4} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Error States</CardTitle>
              <CardDescription>
                User-friendly error messages with recovery actions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ErrorState
                title="Failed to load activities"
                message="We couldn't fetch your activities. Please check your internet connection and try again."
                severity="error"
                onRetry={() => toast({ title: "Retrying..." })}
              />

              <ErrorState
                title="Warning"
                message="Your storage is almost full. Consider backing up your photos."
                severity="warning"
                onDismiss={() => toast({ title: "Dismissed" })}
              />

              <ErrorState
                title="Info"
                message="New features available! Update the app to access them."
                severity="info"
              />

              <div>
                <p className="text-sm font-medium mb-2">Inline Error</p>
                <InlineError message="This field is required" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Success Feedback</CardTitle>
              <CardDescription>
                Checkmarks, points popups, confetti, and streak animations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm font-medium mb-2">Success Checkmark</p>
                <RippleButton onClick={handleShowSuccess}>
                  Show Checkmark
                </RippleButton>
                {showCheckmark && (
                  <div className="mt-4 flex justify-center">
                    <SuccessCheckmark onComplete={() => setShowCheckmark(false)} />
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Points Popup</p>
                <RippleButton onClick={handleShowPoints}>
                  Show Points
                </RippleButton>
                {showPoints && (
                  <div className="mt-4 flex justify-center">
                    <PointsPopup
                      points={50}
                      position={{ x: 200, y: 200 }}
                      reason="Demo"
                      onComplete={() => setShowPoints(false)}
                    />
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Streak Fire</p>
                <div className="flex justify-center py-4">
                  <StreakFire count={7} />
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Confetti</p>
                <RippleButton onClick={handleShowConfetti}>
                  🎉 Celebrate!
                </RippleButton>
                <Confetti trigger={showConfetti} onComplete={() => setShowConfetti(false)} />
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Toast Notifications</p>
                <div className="flex flex-wrap gap-2">
                  <RippleButton
                    onClick={() => toast({
                      title: "Success!",
                      description: "Your activity has been saved",
                    })}
                  >
                    Show Toast
                  </RippleButton>
                  <RippleButton
                    variant="danger"
                    onClick={() => toast({
                      title: "Error",
                      description: "Something went wrong",
                      variant: "destructive",
                    })}
                  >
                    Error Toast
                  </RippleButton>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Pull to Refresh</CardTitle>
              <CardDescription>
                Swipe gesture with visual feedback
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border border-border rounded-lg overflow-hidden">
                <PullToRefresh onRefresh={handleRefresh}>
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div key={i} className="p-3 bg-muted rounded">
                        Item {i + 1}
                      </div>
                    ))}
                  </div>
                </PullToRefresh>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transitions Tab */}
        <TabsContent value="transitions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Screen Transitions</CardTitle>
              <CardDescription>
                Smooth animations for screen mounts and route changes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm font-medium mb-2">Fade In</p>
                <ScreenTransition type="fade">
                  <Card className="p-4">
                    <p className="text-sm">Content fades in smoothly</p>
                  </Card>
                </ScreenTransition>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Slide Up</p>
                <ScreenTransition type="slide-up">
                  <Card className="p-4">
                    <p className="text-sm">Content slides up from bottom</p>
                  </Card>
                </ScreenTransition>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Scale</p>
                <ScreenTransition type="scale">
                  <Card className="p-4">
                    <p className="text-sm">Content scales in with fade</p>
                  </Card>
                </ScreenTransition>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Slide Right</p>
                <ScreenTransition type="slide-right" delay={100}>
                  <Card className="p-4">
                    <p className="text-sm">Content slides from left</p>
                  </Card>
                </ScreenTransition>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Accessibility</CardTitle>
              <CardDescription>
                Focus indicators, touch targets, and reduced motion support
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Focus Indicators</p>
                <div className="flex gap-2">
                  <button className="px-4 py-2 bg-primary text-primary-foreground rounded focus-ring">
                    Keyboard Focus
                  </button>
                  <button className="px-4 py-2 bg-secondary text-secondary-foreground rounded focus-ring">
                    Tab Through
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Try tabbing through these buttons to see focus indicators
                </p>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Touch Targets (≥44×44px)</p>
                <div className="flex gap-2">
                  <button className="touch-target bg-primary text-primary-foreground rounded flex items-center justify-center">
                    <AlertCircle className="w-5 h-5" />
                  </button>
                  <button className="touch-target bg-secondary text-secondary-foreground rounded flex items-center justify-center">
                    <Sparkles className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  All interactive elements meet minimum touch target size
                </p>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Reduced Motion</p>
                <p className="text-xs text-muted-foreground">
                  All animations respect the user's prefers-reduced-motion setting
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
